import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../layout/DashboardLayout';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Bell,
  FileText,
  MessageSquare,
  Upload,
  Sparkles,
  Activity,
  CheckSquare,
  FolderOpen,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  ChevronRight,
  User,
  TrendingUp,
  LifeBuoy,
} from 'lucide-react';
import {
  serviceRequestAPI,
  taskAPI,
  notificationAPI,
  studentAPI,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StudentJourneyTimeline from './StudentJourneyTimeline';
import { formatDistanceToNow } from 'date-fns';
import { getServiceBySlug } from '../../data/serviceData';

// ─── Service type config ──────────────────────────────────────────────────────
const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: {
    label: 'Profile Assessment',
    Icon: Target,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
    accentBg: 'bg-blue-500',
    cardBg: 'bg-blue-50',
    titleColor: 'text-blue-600',
  },
  UNIVERSITY_SHORTLISTING: {
    label: 'Preapplication Support',
    Icon: GraduationCap,
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100',
    accentBg: 'bg-purple-500',
    cardBg: 'bg-purple-50',
    titleColor: 'text-purple-600',
  },
  APPLICATION_ASSISTANCE: {
    label: 'Apply University',
    Icon: FileText,
    iconColor: 'text-pink-600',
    iconBg: 'bg-pink-100',
    accentBg: 'bg-pink-500',
    cardBg: 'bg-pink-50',
    titleColor: 'text-pink-600',
  },
  VISA_GUIDANCE: {
    label: 'Visa & Interview Support',
    Icon: Plane,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-100',
    accentBg: 'bg-orange-500',
    cardBg: 'bg-orange-50',
    titleColor: 'text-orange-500',
  },
  SCHOLARSHIP_SEARCH: {
    label: 'Ticket & Travel Support',
    Icon: DollarSign,
    iconColor: 'text-teal-600',
    iconBg: 'bg-teal-100',
    accentBg: 'bg-teal-500',
    cardBg: 'bg-teal-50',
    titleColor: 'text-teal-600',
  },
  LOAN_ASSISTANCE: {
    label: 'Education Loan',
    Icon: DollarSign,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    accentBg: 'bg-emerald-500',
    cardBg: 'bg-emerald-50',
    titleColor: 'text-emerald-600',
  },
  ACCOMMODATION_HELP: {
    label: 'Find Accommodation',
    Icon: Home,
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
    accentBg: 'bg-rose-500',
    cardBg: 'bg-rose-50',
    titleColor: 'text-rose-600',
  },
  PRE_DEPARTURE_ORIENTATION: {
    label: 'Find Jobs Abroad',
    Icon: Briefcase,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-100',
    accentBg: 'bg-amber-500',
    cardBg: 'bg-amber-50',
    titleColor: 'text-amber-600',
  },
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: {
    label: 'Pending',
    badge: 'bg-slate-100 text-slate-600',
    progress: 10,
    progressBg: 'bg-slate-300',
  },
  ASSIGNED: {
    label: 'Assigned',
    badge: 'bg-blue-50 text-blue-600',
    progress: 30,
    progressBg: 'bg-blue-400',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    badge: 'bg-amber-50 text-amber-700',
    progress: 60,
    progressBg: 'bg-amber-400',
  },
  ON_HOLD: {
    label: 'On Hold',
    badge: 'bg-orange-50 text-orange-600',
    progress: 50,
    progressBg: 'bg-orange-400',
  },
  COMPLETED: {
    label: 'Completed',
    badge: 'bg-emerald-50 text-emerald-700',
    progress: 100,
    progressBg: 'bg-emerald-400',
  },
  CANCELLED: {
    label: 'Cancelled',
    badge: 'bg-red-50 text-red-600',
    progress: 0,
    progressBg: 'bg-red-300',
  },
};

const LEGACY_INTEREST_SERVICE_MAP = {
  'service-1': 'profile-assessment',
  'service-2': 'preapplication-support',
  'service-3': 'apply-university',
  'service-4': 'visa-interview',
  'service-5': 'ticket-travel',
  'service-6': 'accommodation',
  'service-7': 'education-loan',
  'service-8': 'jobs-abroad',
};

const INTEREST_CARD_STYLES = {
  'profile-assessment': {
    shell: 'from-[#efe3ff] via-[#e3d2ff] to-[#d2b7ff]',
    glow: 'shadow-[0_10px_30px_rgba(168,125,250,0.25)]',
    border: 'border-[#9b6ffc]/55',
    tint: 'from-[#a87dfa]/48 via-[#a87dfa]/18 to-transparent',
    title: 'text-[#6d49d4]',
    chip: 'text-[#7a55df]',
    iconWrap: 'border-[#a87dfa]/40 bg-[#f3e9ff]',
    iconColor: 'text-[#7a55df]',
    button:
      'border-[#a87dfa]/45 bg-[#f7efff] text-[#6d49d4] hover:bg-[#efe0ff]',
  },
  'preapplication-support': {
    shell: 'from-[#e4f0ff] via-[#d2e6ff] to-[#bddbff]',
    glow: 'shadow-[0_10px_30px_rgba(111,168,255,0.24)]',
    border: 'border-[#6fa8ff]/55',
    tint: 'from-[#6fa8ff]/48 via-[#6fa8ff]/18 to-transparent',
    title: 'text-[#3f7edc]',
    chip: 'text-[#4a8cf1]',
    iconWrap: 'border-[#6fa8ff]/40 bg-[#edf5ff]',
    iconColor: 'text-[#4a8cf1]',
    button:
      'border-[#6fa8ff]/45 bg-[#edf5ff] text-[#3f7edc] hover:bg-[#dfedff]',
  },
  'apply-university': {
    shell: 'from-[#ffe3ee] via-[#ffd2e5] to-[#ffc0da]',
    glow: 'shadow-[0_10px_30px_rgba(255,155,194,0.24)]',
    border: 'border-[#ff9bc2]/55',
    tint: 'from-[#ff9bc2]/46 via-[#ff9bc2]/18 to-transparent',
    title: 'text-[#d45f93]',
    chip: 'text-[#dd6f9f]',
    iconWrap: 'border-[#ff9bc2]/40 bg-[#fff0f7]',
    iconColor: 'text-[#dd6f9f]',
    button:
      'border-[#ff9bc2]/45 bg-[#fff0f7] text-[#d45f93] hover:bg-[#ffe3f0]',
  },
  'visa-interview': {
    shell: 'from-[#dffaf5] via-[#cdf4eb] to-[#b4eadf]',
    glow: 'shadow-[0_10px_30px_rgba(120,214,198,0.24)]',
    border: 'border-[#78d6c6]/55',
    tint: 'from-[#78d6c6]/46 via-[#78d6c6]/18 to-transparent',
    title: 'text-[#24998a]',
    chip: 'text-[#2aa898]',
    iconWrap: 'border-[#78d6c6]/40 bg-[#ecfffb]',
    iconColor: 'text-[#2aa898]',
    button:
      'border-[#78d6c6]/45 bg-[#ecfffb] text-[#24998a] hover:bg-[#dcfaf3]',
  },
  'ticket-travel': {
    shell: 'from-[#fff0d8] via-[#ffe5be] to-[#ffd497]',
    glow: 'shadow-[0_10px_30px_rgba(255,191,102,0.24)]',
    border: 'border-[#ffbf66]/55',
    tint: 'from-[#ffbf66]/46 via-[#ffbf66]/18 to-transparent',
    title: 'text-[#d28a20]',
    chip: 'text-[#e59a28]',
    iconWrap: 'border-[#ffbf66]/40 bg-[#fff7ea]',
    iconColor: 'text-[#e59a28]',
    button:
      'border-[#ffbf66]/45 bg-[#fff7ea] text-[#d28a20] hover:bg-[#ffeed4]',
  },
  accommodation: {
    shell: 'from-[#e2f7dc] via-[#d1efc8] to-[#b8e4ab]',
    glow: 'shadow-[0_10px_30px_rgba(143,209,127,0.24)]',
    border: 'border-[#8fd17f]/55',
    tint: 'from-[#8fd17f]/46 via-[#8fd17f]/18 to-transparent',
    title: 'text-[#4e9b40]',
    chip: 'text-[#5aa64a]',
    iconWrap: 'border-[#8fd17f]/40 bg-[#f1ffec]',
    iconColor: 'text-[#5aa64a]',
    button:
      'border-[#8fd17f]/45 bg-[#f1ffec] text-[#4e9b40] hover:bg-[#e3f8db]',
  },
  'education-loan': {
    shell: 'from-[#dff3ff] via-[#caeaff] to-[#addbff]',
    glow: 'shadow-[0_10px_30px_rgba(126,200,255,0.24)]',
    border: 'border-[#7ec8ff]/55',
    tint: 'from-[#7ec8ff]/46 via-[#57a8ff]/18 to-transparent',
    title: 'text-[#3b84c7]',
    chip: 'text-[#4794db]',
    iconWrap: 'border-[#7ec8ff]/40 bg-[#edf8ff]',
    iconColor: 'text-[#4794db]',
    button:
      'border-[#7ec8ff]/45 bg-[#edf8ff] text-[#3b84c7] hover:bg-[#dff1ff]',
  },
  'jobs-abroad': {
    shell: 'from-[#ffe4dd] via-[#ffd3c8] to-[#ffbdac]',
    glow: 'shadow-[0_10px_30px_rgba(255,159,139,0.24)]',
    border: 'border-[#ff9f8b]/55',
    tint: 'from-[#ff9f8b]/46 via-[#ff9f8b]/18 to-transparent',
    title: 'text-[#d36852]',
    chip: 'text-[#e17a65]',
    iconWrap: 'border-[#ff9f8b]/40 bg-[#fff1ed]',
    iconColor: 'text-[#e17a65]',
    button:
      'border-[#ff9f8b]/45 bg-[#fff1ed] text-[#d36852] hover:bg-[#ffe2d9]',
  },
};

// ─── "What's Next" config per state ──────────────────────────────────────────
const getNextAction = (serviceRequests, pendingTasksCount, unreadCount) => {
  if (serviceRequests.length === 0)
    return {
      Icon: Sparkles,
      label: "Let's begin",
      title: 'Start your study abroad journey',
      desc: 'Explore our 8 services designed to guide you from application to arrival',
      cta: 'Explore Services',
      link: '/dashboard/services',
      gradientFrom: 'from-[#1C8DEE]',
      gradientTo: 'to-[#38a8f5]',
      iconRing: 'ring-white/20',
      ctaStyle: 'bg-white text-[#1C8DEE] hover:bg-blue-50',
      textColor: 'text-white',
      subColor: 'text-blue-100',
      labelColor: 'text-blue-200',
    };
  if (pendingTasksCount > 0)
    return {
      Icon: CheckSquare,
      label: 'Action needed',
      title: `${pendingTasksCount} pending task${pendingTasksCount !== 1 ? 's' : ''} waiting`,
      desc: 'Complete your assigned tasks to keep your applications moving forward',
      cta: 'View Tasks',
      link: '/dashboard/tasks',
      gradientFrom: 'from-amber-500',
      gradientTo: 'to-orange-500',
      iconRing: 'ring-white/20',
      ctaStyle: 'bg-white text-amber-600 hover:bg-amber-50',
      textColor: 'text-white',
      subColor: 'text-amber-100',
      labelColor: 'text-amber-200',
    };
  if (unreadCount > 0)
    return {
      Icon: Bell,
      label: 'New updates',
      title: `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`,
      desc: 'You have new updates from your counselors and service teams',
      cta: 'View Notifications',
      link: '/dashboard/notifications',
      gradientFrom: 'from-indigo-500',
      gradientTo: 'to-[#1C8DEE]',
      iconRing: 'ring-white/20',
      ctaStyle: 'bg-white text-indigo-600 hover:bg-indigo-50',
      textColor: 'text-white',
      subColor: 'text-indigo-100',
      labelColor: 'text-indigo-200',
    };
  return {
    Icon: CheckCircle,
    label: 'All good',
    title: "You're all caught up!",
    desc: 'Everything is on track — keep monitoring your active services below',
    cta: 'Track Progress',
    link: '/dashboard/track',
    gradientFrom: 'from-emerald-500',
    gradientTo: 'to-teal-500',
    iconRing: 'ring-white/20',
    ctaStyle: 'bg-white text-emerald-600 hover:bg-emerald-50',
    textColor: 'text-white',
    subColor: 'text-emerald-100',
    labelColor: 'text-emerald-200',
  };
};

// ─── Skeleton helpers ─────────────────────────────────────────────────────────
function StatSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm animate-pulse"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-gray-100" />
            <div className="w-6 h-6 rounded-full bg-gray-100" />
          </div>
          <div className="h-8 w-12 bg-gray-100 rounded-lg mb-2" />
          <div className="h-3 w-24 bg-gray-100 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function ServiceRowSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="p-4 rounded-2xl border border-gray-100 animate-pulse"
        >
          <div className="flex items-start justify-between mb-3 pt-1">
            <div className="w-10 h-10 rounded-xl bg-gray-100" />
            <div className="w-16 h-5 bg-gray-100 rounded-full" />
          </div>
          <div className="h-4 w-36 bg-gray-100 rounded mb-2" />
          <div className="h-3 w-24 bg-gray-100 rounded mb-3" />
          <div className="h-1.5 w-full bg-gray-100 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [serviceRequests, setServiceRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAllServices, setShowAllServices] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, notifRes, tasksRes, docsRes, unreadRes, profileRes] =
        await Promise.all([
          serviceRequestAPI.getAll().catch(() => ({ serviceRequests: [] })),
          notificationAPI
            .getAll({ limit: 8 })
            .catch(() => ({ notifications: [] })),
          taskAPI.getAll().catch(() => ({ tasks: [] })),
          studentAPI.getDocuments().catch(() => ({ documents: [] })),
          notificationAPI
            .getUnreadCount()
            .catch(() => ({ count: 0, unreadCount: 0 })),
          studentAPI.getMyProfile().catch(() => ({ student: null })),
        ]);
      setServiceRequests(reqRes.serviceRequests || []);
      setNotifications(notifRes.notifications || []);
      setTasks(tasksRes.tasks || []);
      setDocuments(docsRes.documents || []);
      setUnreadCount(unreadRes.count || unreadRes.unreadCount || 0);
      setStudentProfile(profileRes.student || null);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived stats ─────────────────────────────────────────────────────────
  const activeServicesCount = serviceRequests.filter(sr =>
    ['IN_PROGRESS', 'ASSIGNED'].includes(sr.status),
  ).length;

  const pendingTasksCount = tasks.filter(
    t => !['COMPLETED', 'CANCELLED'].includes((t.status || '').toUpperCase()),
  ).length;

  const documentCount = documents.length;

  const sortedRequests = [...serviceRequests].sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt) -
      new Date(a.updatedAt || a.createdAt),
  );

  const rawInterestedServices =
    studentProfile?.interestedServices?.length > 0
      ? studentProfile.interestedServices
      : (studentProfile?.selectedServices || []).map(
          serviceId => LEGACY_INTEREST_SERVICE_MAP[serviceId] || serviceId,
        );

  const interestedServiceCards = rawInterestedServices
    .map(slug => getServiceBySlug(slug))
    .filter(Boolean);

  // ── Quick actions list ────────────────────────────────────────────────────
  const quickActions = [
    // {
    //   label: 'Explore Services',
    //   desc: 'Browse all available services',
    //   Icon: Sparkles,
    //   iconBg: 'bg-[#EBF5FF]',
    //   iconColor: 'text-[#1C8DEE]',
    //   link: '/dashboard/services',
    // },
    {
      label: 'Track Progress',
      desc: 'View your applications',
      Icon: TrendingUp,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-500',
      link: '/dashboard/track',
    },
    pendingTasksCount > 0
      ? {
          label: 'Complete Tasks',
          desc: `${pendingTasksCount} task${pendingTasksCount !== 1 ? 's' : ''} awaiting you`,
          Icon: CheckSquare,
          iconBg: 'bg-amber-50',
          iconColor: 'text-amber-500',
          link: '/dashboard/tasks',
          urgent: true,
        }
      : {
          label: 'Upload Documents',
          desc: 'Passport, transcripts & more',
          Icon: Upload,
          iconBg: 'bg-teal-50',
          iconColor: 'text-teal-500',
          link: '/dashboard/documents',
        },
    {
      label: 'Contact Support',
      desc: 'Get help from our team',
      Icon: LifeBuoy,
      iconBg: 'bg-gray-50',
      iconColor: 'text-gray-400',
      link: '/dashboard/help',
    },
  ];

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="student-dashboard">
          <div className="h-[92px] rounded-2xl bg-gradient-to-r from-gray-100 to-gray-50 animate-pulse" />
          <ServiceRowSkeleton />
          <StudentJourneyTimeline />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="h-5 w-40 bg-gray-100 rounded animate-pulse mb-5" />
              <StatSkeleton />
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm animate-pulse h-64" />
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm animate-pulse h-52" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const nextAction = getNextAction(
    serviceRequests,
    pendingTasksCount,
    unreadCount,
  );
  const { Icon: HeroIcon } = nextAction;

  // ── STAT CARDS data ───────────────────────────────────────────────────────
  const STAT_CARDS = [
    {
      key: 'services',
      label: 'Active Services',
      value: activeServicesCount,
      Icon: Activity,
      cardGradient:
        'bg-gradient-to-br from-cyan-100 to-sky-50/80 border border-sky-200/80',
      iconGradient:
        'bg-gradient-to-br from-sky-500 to-blue-600 shadow-lg shadow-sky-500/20',
      badge: 'Active',
      badgeClass: 'bg-sky-50 text-sky-700 border-sky-200',
      btnColor: 'text-sky-600',
      delay: 0.1,
      link: '/dashboard/track',
    },
    {
      key: 'tasks',
      label: 'Pending Tasks',
      value: pendingTasksCount,
      Icon: CheckSquare,
      cardGradient:
        'bg-gradient-to-br from-violet-100 to-violet-50/80 border border-violet-200/80',
      iconGradient:
        'bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20',
      badge: pendingTasksCount > 0 ? 'Urgent' : 'Active',
      badgeClass:
        pendingTasksCount > 0
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-violet-50 text-violet-700 border-violet-200',
      btnColor: 'text-violet-600',
      delay: 0.2,
      link: '/dashboard/tasks',
    },
    {
      key: 'messages',
      label: 'Unread Messages',
      value: unreadCount,
      Icon: MessageSquare,
      cardGradient:
        'bg-gradient-to-br from-amber-100 to-amber-50/80 border border-amber-200/80',
      iconGradient:
        'bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/20',
      badge: unreadCount > 0 ? 'New' : '0%',
      badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
      btnColor: 'text-amber-600',
      delay: 0.3,
      link: '/dashboard/messages',
    },
    {
      key: 'docs',
      label: 'Documents',
      value: documentCount,
      Icon: FolderOpen,
      cardGradient:
        'bg-gradient-to-br from-emerald-100 to-emerald-50/80 border border-emerald-200/80',
      iconGradient:
        'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/20',
      badge: `${documentCount} files`,
      badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      btnColor: 'text-emerald-600',
      delay: 0.4,
      link: '/dashboard/documents',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="student-dashboard">
        {/* ══════════════════════════════════════════════
            Section 1 — Journey Timeline
        ══════════════════════════════════════════════ */}
        <StudentJourneyTimeline />

        {/* ══════════════════════════════════════════════
            Section 3 — Smart Stats Row
        ══════════════════════════════════════════════ */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white shadow-md shadow-sky-100/50 p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STAT_CARDS.map(card => {
              const CardIcon = card.Icon;
              return (
                <motion.div
                  key={card.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: card.delay }}
                  onClick={() => navigate(card.link)}
                  className="cursor-pointer group"
                >
                  <Card
                    className={`p-6 ${card.cardGradient} rounded-2xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className={`w-12 h-12 ${card.iconGradient} rounded-2xl flex items-center justify-center`}
                      >
                        <CardIcon className="w-6 h-6 text-white" />
                      </div>
                      <Badge
                        className={`${card.badgeClass} rounded-full text-xs font-semibold px-2.5`}
                      >
                        {card.badge}
                      </Badge>
                    </div>
                    <p className="text-3xl font-bold text-slate-900 mb-1">
                      {card.value}
                    </p>
                    <p className="text-sm text-slate-500 font-medium mb-4">
                      {card.label}
                    </p>
                    <div
                      className={`flex items-center gap-1.5 text-xs font-semibold ${card.btnColor} group-hover:gap-2.5 transition-all duration-200`}
                    >
                      View Details <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {interestedServiceCards.length > 0 && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white shadow-md shadow-violet-100/50 p-5">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-[18px] font-bold text-slate-900">
                  Your Interested Services
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  These are the services you selected during onboarding. Explore
                  them and apply whenever you&apos;re ready.
                </p>
              </div>
              <button
                className="text-[13px] font-semibold text-[#1C8DEE] hover:text-[#1a7fd4] transition-colors"
                onClick={() => navigate('/dashboard/services')}
              >
                VIEW ALL
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {interestedServiceCards.map((service, idx) => {
                const visuals = INTEREST_CARD_STYLES[service.slug] || {};

                return (
                  <motion.div
                    key={service.slug}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: idx * 0.05,
                      duration: 0.28,
                      ease: 'easeOut',
                    }}
                    onClick={() =>
                      navigate(`/dashboard/services/${service.slug}`)
                    }
                    className={`partner-card partner-card-hover relative flex h-full cursor-pointer flex-col rounded-[28px] border bg-gradient-to-br p-4 ${visuals.shell || 'from-white/70 to-white/40'} ${visuals.border || 'border-[#A87DFA]/24'} ${visuals.glow || ''}`}
                  >
                    <div
                      className={`absolute inset-x-0 top-0 h-28 rounded-t-[28px] bg-gradient-to-b ${visuals.tint || 'from-[#A87DFA]/20 via-[#A87DFA]/8 to-transparent'} pointer-events-none`}
                    />
                    <div className="relative mb-4 overflow-hidden rounded-[22px] border border-[#A87DFA]/18">
                      <img
                        src={service.heroImage}
                        alt={service.name}
                        className="h-40 w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.18))]" />
                      <div className="absolute left-4 top-4 flex h-14 w-14 items-center justify-center rounded-[18px] border border-white/25 bg-white/20 backdrop-blur-md">
                        <img
                          src={service.heroIcon}
                          alt=""
                          className="h-8 w-8 object-contain"
                        />
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <div
                          className={`rounded-full border border-white/35 bg-white/55 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] backdrop-blur-md ${visuals.chip || 'text-[#7A55DF]'}`}
                        >
                          Fly8 Service
                        </div>
                      </div>
                    </div>
                    <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[#8D68E8]">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-2xl border ${visuals.iconWrap || 'border-[#A87DFA]/16 bg-[linear-gradient(135deg,rgba(168,125,250,0.16),rgba(168,125,250,0.05))]'}`}
                      >
                        {service.icon ? (
                          <service.icon
                            className={`h-4 w-4 ${visuals.iconColor || 'text-[#7A55DF]'}`}
                          />
                        ) : (
                          <img
                            src={service.heroIcon}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                        )}
                      </div>
                      <span className={visuals.chip || 'text-[#7A55DF]'}>
                        Interested Service
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <h3
                        className={`mb-2 text-xl font-semibold ${visuals.title || 'text-[#6d49d4]'}`}
                      >
                        {service.name}
                      </h3>

                      <button
                        type="button"
                        className={`group mt-4 w-full rounded-2xl border bg-white/65 px-4 py-2.5 text-[13px] font-semibold transition-colors ${visuals.button || 'partner-secondary-btn'}`}
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/dashboard/services/${service.slug}`);
                        }}
                      >
                        <span className="inline-flex items-center justify-center gap-1">
                          View Service
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            Section 4 — Services in Progress
        ══════════════════════════════════════════════ */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white shadow-md shadow-violet-100/50 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-bold text-slate-900">
              Services in Progress
              {sortedRequests.length > 0 && (
                <span className="ml-2 text-[12px] font-semibold text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded-full align-middle">
                  {sortedRequests.length}
                </span>
              )}
            </h2>
            <button
              className="text-[13px] font-semibold text-[#1C8DEE] hover:text-[#1a7fd4] transition-colors"
              onClick={() => navigate('/dashboard/track')}
            >
              VIEW ALL
            </button>
          </div>

          {sortedRequests.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {(showAllServices
                  ? sortedRequests
                  : sortedRequests.slice(0, 3)
                ).map((sr, idx) => {
                  const cfg = SERVICE_TYPE_CONFIG[sr.serviceType] || {
                    label: sr.serviceType || 'Service',
                    Icon: Target,
                    iconColor: 'text-gray-500',
                    iconBg: 'bg-gray-100',
                    accentBg: 'bg-gray-300',
                    cardBg: 'bg-gray-50',
                    titleColor: 'text-gray-700',
                  };
                  const statusCfg = STATUS_CONFIG[sr.status] || {
                    label: 'Unknown',
                    badge: 'bg-gray-100 text-gray-500',
                    progress: 20,
                    progressBg: 'bg-gray-300',
                  };
                  const ServiceIcon = cfg.Icon;
                  const counselor = sr.assignedCounselor;
                  const counselorName = counselor
                    ? `${counselor.firstName || ''} ${counselor.lastName || ''}`.trim()
                    : null;

                  // Richer pastel card gradients matching the screenshot palette
                  const cardPalette = [
                    'from-violet-100/90 via-purple-50 to-violet-50/60',
                    'from-pink-100/90 via-rose-50 to-pink-50/60',
                    'from-sky-100/90 via-blue-50 to-sky-50/60',
                    'from-emerald-100/90 via-green-50 to-emerald-50/60',
                    'from-amber-100/90 via-yellow-50 to-amber-50/60',
                    'from-indigo-100/90 via-blue-50 to-indigo-50/60',
                  ];
                  const shadowPalette = [
                    'hover:shadow-violet-200/60',
                    'hover:shadow-pink-200/60',
                    'hover:shadow-sky-200/60',
                    'hover:shadow-emerald-200/60',
                    'hover:shadow-amber-200/60',
                    'hover:shadow-indigo-200/60',
                  ];
                  const palette = cardPalette[idx % cardPalette.length];
                  const shadow = shadowPalette[idx % shadowPalette.length];

                  return (
                    <motion.div
                      key={sr.serviceRequestId}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: idx * 0.06,
                        duration: 0.3,
                        ease: 'easeOut',
                      }}
                      onClick={() =>
                        navigate(`/dashboard/service/${sr.serviceRequestId}`)
                      }
                      className={`relative bg-gradient-to-br ${palette} rounded-3xl p-5 cursor-pointer hover:shadow-xl ${shadow} hover:-translate-y-1 transition-all duration-300 group overflow-hidden`}
                    >
                      {/* Decorative circle blob */}
                      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/30 pointer-events-none" />

                      {/* Top: status badge + icon */}
                      <div className="flex items-start justify-between mb-4">
                        <span
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/70 backdrop-blur-sm shadow-sm ${statusCfg.badge}`}
                        >
                          {statusCfg.label}
                        </span>
                        <div
                          className={`w-11 h-11 rounded-2xl ${cfg.iconBg} flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300`}
                        >
                          <ServiceIcon className={`w-5 h-5 ${cfg.iconColor}`} />
                        </div>
                      </div>

                      {/* Service name */}
                      <h4 className="text-[15px] font-bold text-slate-800 mb-1 leading-snug">
                        {cfg.label}
                      </h4>

                      {/* Counselor */}
                      <p className="text-[11px] text-slate-500 mb-4 truncate">
                        {counselorName
                          ? `with ${counselorName}`
                          : 'Awaiting counselor'}
                      </p>

                      {/* Progress */}
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold text-slate-500">
                          Progress
                        </span>
                        <span
                          className={`text-[11px] font-bold ${cfg.iconColor}`}
                        >
                          {statusCfg.progress}%
                        </span>
                      </div>
                      <div className="w-full h-[6px] bg-white/60 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${statusCfg.progressBg}`}
                          style={{ width: `${statusCfg.progress}%` }}
                        />
                      </div>

                      {/* Date footer */}
                      <p
                        className={`text-[10px] font-semibold mt-3 ${cfg.iconColor} opacity-70`}
                      >
                        {sr.createdAt
                          ? `Started ${new Date(sr.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`
                          : 'Active'}
                      </p>
                    </motion.div>
                  );
                })}

                {/* Explore promo card */}
                {!showAllServices && sortedRequests.length < 3 && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18, duration: 0.3 }}
                    onClick={() => navigate('/dashboard/services')}
                    className="relative bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 rounded-3xl p-5 cursor-pointer hover:shadow-xl hover:shadow-orange-200/60 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between min-h-[200px] overflow-hidden"
                  >
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/15 pointer-events-none" />
                    <div>
                      <div className="w-11 h-11 rounded-2xl bg-white/25 flex items-center justify-center mb-4">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-[15px] font-bold text-white mb-1.5">
                        Explore Services
                      </h4>
                      <p className="text-[11px] text-amber-100 leading-relaxed">
                        Discover all 8 services designed for your study abroad
                        journey
                      </p>
                    </div>
                    <button
                      className="mt-5 self-start px-4 py-2 bg-white text-amber-600 rounded-xl text-[12px] font-bold hover:bg-amber-50 transition-colors shadow-sm"
                      onClick={e => {
                        e.stopPropagation();
                        navigate('/dashboard/services');
                      }}
                    >
                      Browse Services
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Show More / Show Less */}
              {sortedRequests.length > 3 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowAllServices(prev => !prev)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-violet-50 via-purple-50 to-pink-50 border border-violet-200/60 text-[13px] font-semibold text-violet-600 hover:from-violet-100 hover:to-pink-100 transition-all duration-200 shadow-sm"
                  >
                    {showAllServices ? (
                      <>
                        Show Less{' '}
                        <ChevronRight className="w-4 h-4 rotate-[-90deg]" />
                      </>
                    ) : (
                      <>
                        Show More ({sortedRequests.length - 3} more){' '}
                        <ChevronRight className="w-4 h-4 rotate-90" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gradient-to-br from-violet-50 via-blue-50 to-sky-50 rounded-3xl flex flex-col items-center justify-center py-16 px-8 text-center border border-violet-100/60">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-blue-500 flex items-center justify-center mb-4 shadow-lg shadow-violet-300/40">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-[15px] font-bold text-slate-900 mb-1.5">
                No active services yet
              </h4>
              <p className="text-sm text-slate-400 max-w-xs mb-5">
                Apply for any of our 8 services to begin your study abroad
                journey
              </p>
              <Button
                className="bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 text-white rounded-xl px-6 text-sm font-semibold shadow-md shadow-violet-300/40 transition-all duration-200"
                onClick={() => navigate('/dashboard/services')}
              >
                Explore Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════
            Section 5 — Quick Actions + Recent Activity
        ══════════════════════════════════════════════ */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white shadow-md shadow-pink-100/50 p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* ── Quick Actions ── */}
            <div>
              <h2 className="text-[18px] font-bold text-slate-900 mb-4">
                Quick Actions
              </h2>
              <div className="flex flex-col gap-3">
                {quickActions.map((action, i) => {
                  const ActionIcon = action.Icon;
                  const rowPalette = [
                    {
                      card: 'from-sky-50 to-blue-50/80 border-sky-200/60 hover:shadow-sky-100',
                      text: 'text-sky-700',
                      icon: 'bg-sky-100',
                    },
                    {
                      card: 'from-violet-50 to-purple-50/80 border-violet-200/60 hover:shadow-violet-100',
                      text: 'text-violet-700',
                      icon: 'bg-violet-100',
                    },
                    {
                      card: 'from-amber-50 to-orange-50/80 border-amber-200/60 hover:shadow-amber-100',
                      text: 'text-amber-700',
                      icon: 'bg-amber-100',
                    },
                    {
                      card: 'from-slate-50 to-gray-50/80 border-slate-200/60 hover:shadow-slate-100',
                      text: 'text-slate-600',
                      icon: 'bg-slate-100',
                    },
                  ];
                  const p = rowPalette[i % rowPalette.length];
                  return (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.07, duration: 0.25 }}
                      onClick={() => navigate(action.link)}
                      className={`w-full flex items-center gap-3.5 px-4 py-3.5 bg-gradient-to-r ${p.card} border rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group text-left`}
                    >
                      <div
                        className={`w-11 h-11 rounded-xl ${p.icon} flex items-center justify-center flex-shrink-0 shadow-sm`}
                      >
                        <ActionIcon className={`w-5 h-5 ${p.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-[13px] font-bold leading-tight ${p.text}`}
                        >
                          {action.label}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                          {action.desc}
                        </p>
                      </div>
                      <ChevronRight
                        className={`w-4 h-4 ${p.text} opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0`}
                      />
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* ── Recent Activity ── */}
            <div
              className="lg:col-span-2 rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden"
              style={{
                background:
                  'linear-gradient(160deg, #f3f1fd 0%, #edf3fd 50%, #f5f9ff 100%)',
              }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-sky-100/50">
                <h3 className="text-[15px] font-bold text-gray-900">
                  Recent Activity
                </h3>
                <button
                  className="text-[13px] font-semibold text-[#1C8DEE] hover:text-[#1a7fd4] transition-colors"
                  onClick={() => navigate('/dashboard/notifications')}
                >
                  View All
                </button>
              </div>

              {notifications.length > 0 ? (
                <div className="divide-y divide-sky-100/50">
                  {notifications.slice(0, 6).map((notif, i) => {
                    const isSuccess =
                      notif.type === 'SUCCESS' ||
                      notif.type === 'TASK_COMPLETED';
                    const isWarning = notif.type === 'WARNING';
                    return (
                      <div
                        key={notif.notificationId || i}
                        className="flex items-center gap-4 px-5 py-3.5 hover:bg-white/40 transition-colors group"
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                            isSuccess
                              ? 'bg-emerald-50'
                              : isWarning
                                ? 'bg-amber-50'
                                : 'bg-[#EBF5FF]'
                          }`}
                        >
                          {isSuccess ? (
                            <CheckCircle className="w-[17px] h-[17px] text-emerald-500" />
                          ) : isWarning ? (
                            <AlertCircle className="w-[17px] h-[17px] text-amber-500" />
                          ) : (
                            <Bell className="w-[17px] h-[17px] text-[#1C8DEE]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-gray-800 truncate">
                            {notif.title}
                          </p>
                          {notif.message && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                              {notif.message}
                            </p>
                          )}
                        </div>
                        <span
                          className={`hidden sm:inline-flex text-[10px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                            isSuccess
                              ? 'bg-emerald-50 text-emerald-600'
                              : isWarning
                                ? 'bg-amber-50 text-amber-600'
                                : 'bg-blue-50 text-[#1C8DEE]'
                          }`}
                        >
                          {isSuccess
                            ? 'Success'
                            : isWarning
                              ? 'Warning'
                              : 'Info'}
                        </span>
                        <span className="text-[11px] text-gray-300 whitespace-nowrap flex-shrink-0 hidden md:block">
                          {notif.createdAt
                            ? formatDistanceToNow(new Date(notif.createdAt), {
                                addSuffix: true,
                              })
                            : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-14 px-5 text-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mb-3">
                    <Bell className="w-5 h-5 text-gray-200" />
                  </div>
                  <p className="text-[13px] font-semibold text-gray-400">
                    No activity yet
                  </p>
                  <p className="text-[12px] text-gray-300 mt-0.5">
                    Updates will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
